import * as yup from 'yup'
import React from 'react'
import { suffixes } from '../choices'
import { Divider } from '../components/divider'
import {
  complainantContactReducer,
  hasComplainantContactError,
  validContact,
  removeFieldsFromArray,
  removeObjectKeys
} from '../utilities'
import {
  addressLabel,
  address2Label,
  cellPhoneLabel,
  dobLabel,
  employerLabel,
  employerPhoneLabel,
  firstNameLabel,
  homeAddressLabel,
  homePhoneLabel,
  jobTitleLabel,
  medicareNumberLabel,
  medicaidNumberLabel,
  middleNameLabel,
  lastNameLabel,
  optionalLabel,
  personalEmailLabel,
  ssnLabel,
  suffixLabel,
  workEmailLabel,
  workPhoneLabel
} from '../strings/labels'
import {
  addressLengthMessage,
  cityLengthMessage,
  cellPhoneMessage,
  employerPhoneMessage,
  homePhoneMessage,
  jobTitleLengthMessage,
  maxLength150Message,
  personalEmailMessage,
  personalEmailLengthMessage,
  ssnMessage,
  workEmailLengthMessage,
  workEmailMessage,
  workPhoneMessage,
  zipMessage,
  zipLengthMessage
} from '../strings/messages'
import { phoneRegex, ssnRegex, zipRegex } from '../strings/regex'
import {
  dateYupOptional,
  firstNameYup,
  middleNameYup,
  lastNameYup,
  medicareYup,
  medicaidYup
} from '../validators'

const NameHeading = () => <h2 className="ds-u-margin-bottom--0">Your name</h2>

export const ContactInformationHeading = props => (
  <React.Fragment>
    <h2 className="ds-u-margin-bottom--0">Your contact information</h2>
    <p>
      Please provide as much contact information as possible. We need a phone
      number, email address, or home address to contact you.
    </p>
    {hasComplainantContactError(props) && (
      <p className="ds-u-color--error ds-u-margin-bottom--0">
        Contact information is required.
      </p>
    )}
  </React.Fragment>
)

const HomeAddressHeading = () => (
  <h2 className="ds-u-margin-bottom--0">{homeAddressLabel}</h2>
)

const EmployerInformationHeading = () => (
  <h2 className="ds-u-margin-bottom--0">Optional: Employer information</h2>
)

export const mapContactValidationFields = (values, field, fieldValue) => {
  const mappedObj = complainantContactReducer(values)
  return { ...mappedObj, [field]: fieldValue }
}

export const complainantPage = (props = {}) => {
  const { excludeFields, ...pageOverrides } = props

  const defaultValidation = {
    complainant_first_name: firstNameYup(),
    complainant_middle_name: middleNameYup(),
    complainant_last_name: lastNameYup(),
    complainant_medicare_number: medicareYup(),
    complainant_medicaid_number: medicaidYup(),
    complainant_dob: dateYupOptional(),
    complainant_ssn: yup.string().matches(ssnRegex, {
      message: ssnMessage,
      excludeEmptyString: false
    }),
    complainant_address: yup.object().shape({
      line1Value: yup.string().max(50, addressLengthMessage),
      line2Value: yup.string().max(50, addressLengthMessage),
      cityValue: yup.string().max(30, cityLengthMessage),
      zipValue: yup
        .string()
        .max(10, zipLengthMessage)
        .test('zipcode_required', zipMessage, function(value) {
          const { countryValue } = this.parent
          return countryValue === 'US'
            ? value === '' || zipRegex.test(value)
            : true
        })
    }),
    complainant_home_phone: yup
      .string()
      .matches(phoneRegex, {
        message: homePhoneMessage,
        excludeEmptyString: false
      })
      .test('phone_contact_required', homePhoneMessage, function(value) {
        const {
          complainant_cell_phone,
          complainant_address,
          complainant_personal_email,
          complainant_work_email,
          complainant_work_phone
        } = this.parent
        return validContact({
          home_phone: value,
          cell_phone: complainant_cell_phone,
          work_phone: complainant_work_phone,
          email: complainant_personal_email,
          work_email: complainant_work_email,
          address: complainant_address.line1Value,
          city: complainant_address.cityValue,
          state: complainant_address.stateValue,
          zip: complainant_address.zipValue
        })
      }),
    complainant_cell_phone: yup
      .string()
      .matches(phoneRegex, {
        message: cellPhoneMessage,
        excludeEmptyString: false
      })
      .test('phone_contact_required', cellPhoneMessage, function(value) {
        const {
          complainant_address,
          complainant_personal_email,
          complainant_home_phone,
          complainant_work_email,
          complainant_work_phone
        } = this.parent
        return validContact({
          home_phone: complainant_home_phone,
          cell_phone: value,
          work_phone: complainant_work_phone,
          email: complainant_personal_email,
          work_email: complainant_work_email,
          address: complainant_address.line1Value,
          city: complainant_address.cityValue,
          state: complainant_address.stateValue,
          zip: complainant_address.zipValue
        })
      }),
    complainant_work_phone: yup
      .string()
      .matches(phoneRegex, {
        message: workPhoneMessage,
        excludeEmptyString: false
      })
      .test('phone_contact_required', workPhoneMessage, function(value) {
        const {
          complainant_cell_phone,
          complainant_address,
          complainant_personal_email,
          complainant_home_phone,
          complainant_work_email
        } = this.parent
        return validContact({
          home_phone: complainant_home_phone,
          cell_phone: complainant_cell_phone,
          work_phone: value,
          email: complainant_personal_email,
          work_email: complainant_work_email,
          address: complainant_address.line1Value,
          city: complainant_address.cityValue,
          state: complainant_address.stateValue,
          zip: complainant_address.zipValue
        })
      }),
    complainant_job_title: yup.string().max(30, jobTitleLengthMessage),
    complainant_personal_email: yup
      .string()
      .email(personalEmailMessage)
      .max(250, personalEmailLengthMessage)
      .test('email_contact_required', personalEmailMessage, function(value) {
        const {
          complainant_address,
          complainant_cell_phone,
          complainant_home_phone,
          complainant_work_email,
          complainant_work_phone
        } = this.parent
        return validContact({
          email: value,
          home_phone: complainant_home_phone,
          cell_phone: complainant_cell_phone,
          work_phone: complainant_work_phone,
          work_email: complainant_work_email,
          address: complainant_address.line1Value,
          city: complainant_address.cityValue,
          state: complainant_address.stateValue,
          zip: complainant_address.zipValue
        })
      }),
    complainant_work_email: yup
      .string()
      .email(workEmailMessage)
      .max(250, workEmailLengthMessage)
      .test('email_contact_required', workEmailMessage, function(value) {
        const {
          complainant_address,
          complainant_cell_phone,
          complainant_personal_email,
          complainant_home_phone,
          complainant_work_phone
        } = this.parent
        return validContact({
          work_email: value,
          email: complainant_personal_email,
          home_phone: complainant_home_phone,
          cell_phone: complainant_cell_phone,
          work_phone: complainant_work_phone,
          address: complainant_address.line1Value,
          city: complainant_address.cityValue,
          state: complainant_address.stateValue,
          zip: complainant_address.zipValue
        })
      }),
    complainant_employer: yup.string().max(150, maxLength150Message),
    complainant_employer_phone: yup.string().matches(phoneRegex, {
      message: employerPhoneMessage,
      excludeEmptyString: false
    }),
    complainant_work_address: yup.object().shape({
      line1Value: yup.string().max(50, addressLengthMessage),
      line2Value: yup.string().max(50, addressLengthMessage),
      cityValue: yup.string().max(30, cityLengthMessage),
      zipValue: yup
        .string()
        .max(10, zipLengthMessage)
        .test('zipcode_required', zipMessage, function(value) {
          const { countryValue } = this.parent
          return countryValue === 'US'
            ? value === '' || zipRegex.test(value)
            : true
        })
    })
  }

  const defaultFields = [
    {
      type: 'content',
      content: NameHeading,
      name: 'name'
    },
    {
      type: 'text',
      label: firstNameLabel,
      name: 'complainant_first_name'
    },
    {
      type: 'text',
      label: middleNameLabel,
      name: 'complainant_middle_name',
      requirementLabel: optionalLabel
    },
    {
      type: 'text',
      label: lastNameLabel,
      name: 'complainant_last_name'
    },
    {
      type: 'select',
      label: suffixLabel,
      name: 'complainant_suffix',
      choices: [
        {
          value: '',
          label: ''
        },
        ...suffixes()
      ],
      requirementLabel: optionalLabel
    },
    {
      type: 'content',
      content: Divider,
      name: 'personal_info_divider'
    },
    {
      type: 'date',
      label: dobLabel,
      name: 'complainant_dob',
      requirementLabel: optionalLabel,
      overrideDefaultMinMax: true
    },
    {
      type: 'text',
      label: ssnLabel,
      name: 'complainant_ssn',
      requirementLabel: optionalLabel
    },
    {
      type: 'text',
      label: medicareNumberLabel,
      name: 'complainant_medicare_number',
      requirementLabel: optionalLabel
    },
    {
      type: 'text',
      label: medicaidNumberLabel,
      name: 'complainant_medicaid_number',
      requirementLabel: optionalLabel
    },
    {
      type: 'content',
      content: Divider,
      name: 'contact_info_divider'
    },
    {
      type: 'content',
      content: ContactInformationHeading,
      name: 'contact_information'
    },
    {
      type: 'text',
      label: homePhoneLabel,
      name: 'complainant_home_phone'
    },
    {
      type: 'text',
      label: cellPhoneLabel,
      name: 'complainant_cell_phone'
    },
    {
      type: 'text',
      label: workPhoneLabel,
      name: 'complainant_work_phone'
    },
    {
      type: 'text',
      label: personalEmailLabel,
      name: 'complainant_personal_email'
    },
    {
      type: 'text',
      label: workEmailLabel,
      name: 'complainant_work_email'
    },
    {
      type: 'content',
      content: Divider,
      name: 'divider2'
    },
    {
      type: 'content',
      content: HomeAddressHeading,
      name: 'mailing_address'
    },
    {
      type: 'address',
      name: 'complainant_address',
      label: '',
      hint: '',
      line1Label: addressLabel,
      line2Label: address2Label,
      countryValue: 'US'
    },
    {
      type: 'content',
      content: Divider,
      name: 'divider3'
    },
    {
      type: 'content',
      content: EmployerInformationHeading,
      name: 'employer_information'
    },
    {
      type: 'text',
      label: jobTitleLabel,
      name: 'complainant_job_title'
    },
    {
      type: 'text',
      label: employerLabel,
      name: 'complainant_employer'
    },
    {
      type: 'address',
      name: 'complainant_work_address',
      label: '',
      hint: '',
      line1Label: addressLabel,
      line2Label: address2Label,
      countryValue: 'US'
    },
    {
      type: 'text',
      label: employerPhoneLabel,
      name: 'complainant_employer_phone',
      size: 'medium'
    }
  ]

  const hasExcludeFields = Array.isArray(excludeFields)

  const validationShape = hasExcludeFields
    ? removeObjectKeys(excludeFields, defaultValidation, 'complainant_{}')
    : defaultValidation

  const fields = hasExcludeFields
    ? removeFieldsFromArray(excludeFields, defaultFields, 'complainant_{}')
    : defaultFields

  return {
    id: 'complainant_page',
    title: 'Your details',
    description:
      'We need some details about you in order for us to start an investigation. Please provide as much detail as you can.',
    ...pageOverrides,
    validation: yup.object().shape(validationShape),
    fields
  }
}
